/*
 * Copyright (c) 2026 Paxia AI Pty Ltd. All rights reserved.
 * This software is proprietary and confidential.
 * No part of this codebase may be copied, modified, published, distributed, sublicensed, or used without prior written permission from Paxia AI Pty Ltd.
 */

import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  CircularProgress,
  Alert,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Divider,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Error as ErrorIcon,
  CheckCircle as SuccessIcon,
  TrendingDown as TrendingDownIcon,
  TrendingUp as TrendingUpIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  Visibility as ViewIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { errorAnalyticsApi } from '../../api/errorAnalytics';
import { formatStoredDateTime } from '../../utils/datetime';
import { ErrorAnalytics, ErrorDetail, RecentError } from '../../types';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
  BarChart,
  Bar,
} from 'recharts';

const COLORS = ['#f44336', '#ff9800', '#ffc107', '#4caf50', '#2196f3', '#9c27b0'];

interface StatCardProps {
  title: string;
  value: string | number;
  subtitle?: string;
  icon: React.ReactNode;
  color?: string;
  trend?: 'up' | 'down' | 'neutral';
}

const StatCard: React.FC<StatCardProps> = ({ title, value, subtitle, icon, color = 'primary.main', trend }) => {
  const getTrendIcon = () => {
    if (trend === 'up') return <TrendingUpIcon color="error" fontSize="small" />;
    if (trend === 'down') return <TrendingDownIcon color="success" fontSize="small" />;
    return null;
  };

  return (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Box sx={{ color, mr: 2 }}>
              {icon}
            </Box>
            <Typography variant="h6" component="h3">
              {title}
            </Typography>
          </Box>
          {getTrendIcon()}
        </Box>
        <Typography variant="h4" component="div" sx={{ mb: 1 }}>
          {value}
        </Typography>
        {subtitle && (
          <Typography variant="body2" color="text.secondary">
            {subtitle}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
};

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => {
  return (
    <div role="tabpanel" hidden={value !== index}>
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
};

const ErrorDashboard: React.FC = () => {
  const [days, setDays] = useState<number>(30);
  const [selectedError, setSelectedError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState(0);

  const { data, isLoading, error, refetch } = useQuery<ErrorAnalytics>({
    queryKey: ['error-analytics', days],
    queryFn: () => errorAnalyticsApi.getDashboard(days),
    refetchInterval: 60000, // Refetch every minute
  });

  // Debug logging
  console.log('Error Analytics Query State:', { data, isLoading, error });

  const { data: errorDetail, isLoading: isLoadingDetail } = useQuery<ErrorDetail>({
    queryKey: ['error-detail', selectedError],
    queryFn: () => errorAnalyticsApi.getErrorDetail(selectedError!),
    enabled: !!selectedError,
  });

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Typography variant="h4" component="h1" sx={{ mb: 3 }}>
          Error Monitoring Dashboard
        </Typography>
        <Alert severity="error">
          Failed to load error analytics: {error instanceof Error ? error.message : JSON.stringify(error)}
        </Alert>
      </Box>
    );
  }

  if (!data || !data.summary) {
    return (
      <Box>
        <Typography variant="h4" component="h1" sx={{ mb: 3 }}>
          Error Monitoring Dashboard
        </Typography>
        <Alert severity="info">
          {!data ? (
            <>No error data available. The API may not be responding or there are no errors to display yet.</>
          ) : (
            <>Invalid response structure received from the API.</>
          )}
        </Alert>
      </Box>
    );
  }

  // Provide safe defaults for arrays
  const errorTypes = data.error_types || [];
  const errorByExpert = data.error_by_expert || [];
  const errorBySystem = data.error_by_system || [];
  const dailyTrend = data.daily_trend || [];
  const recentErrors = data.recent_errors || [];

  const getErrorTypeColor = (errorType: string): 'error' | 'warning' | 'info' | 'default' => {
    if (errorType.includes('validation')) return 'warning';
    if (errorType.includes('api') || errorType.includes('llm')) return 'error';
    if (errorType.includes('action')) return 'info';
    return 'default';
  };

  const getErrorTypeIcon = (errorType: string) => {
    if (errorType.includes('validation')) return <WarningIcon fontSize="small" />;
    if (errorType.includes('api') || errorType.includes('llm')) return <ErrorIcon fontSize="small" />;
    return <InfoIcon fontSize="small" />;
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Error Monitoring Dashboard
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Time Range</InputLabel>
            <Select
              value={days}
              label="Time Range"
              onChange={(e) => setDays(Number(e.target.value))}
            >
              <MenuItem value={7}>Last 7 days</MenuItem>
              <MenuItem value={30}>Last 30 days</MenuItem>
              <MenuItem value={90}>Last 90 days</MenuItem>
            </Select>
          </FormControl>
          <Tooltip title="Refresh">
            <IconButton onClick={() => refetch()} size="small">
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, md: 3 }}>
          <StatCard
            title="Total Errors"
            value={data.summary.total_errors.toLocaleString()}
            subtitle={`Out of ${data.summary.total_executions.toLocaleString()} executions`}
            icon={<ErrorIcon fontSize="large" />}
            color="error.main"
          />
        </Grid>
        <Grid size={{ xs: 12, md: 3 }}>
          <StatCard
            title="Error Rate"
            value={`${data.summary.error_rate.toFixed(2)}%`}
            subtitle={`Last ${data.summary.days_analyzed} days`}
            icon={<TrendingUpIcon fontSize="large" />}
            color={data.summary.error_rate > 10 ? 'error.main' : 'warning.main'}
            trend={data.summary.error_rate > 10 ? 'up' : 'neutral'}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 3 }}>
          <StatCard
            title="Success Rate"
            value={`${(100 - data.summary.error_rate).toFixed(2)}%`}
            subtitle={`${data.summary.success_count.toLocaleString()} successful`}
            icon={<SuccessIcon fontSize="large" />}
            color="success.main"
            trend="neutral"
          />
        </Grid>
        <Grid size={{ xs: 12, md: 3 }}>
          <StatCard
            title="Error Types"
            value={errorTypes.length}
            subtitle="Unique error categories"
            icon={<WarningIcon fontSize="large" />}
            color="warning.main"
          />
        </Grid>
      </Grid>

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs value={activeTab} onChange={(_, val) => setActiveTab(val)}>
          <Tab label="Overview" />
          <Tab label="Error Types" />
          <Tab label="By Expert" />
          <Tab label="By System" />
          <Tab label="By Agent" />
          <Tab label="Recent Errors" />
        </Tabs>

        {/* Overview Tab */}
        <TabPanel value={activeTab} index={0}>
          <Grid container spacing={3}>
            {/* Error Trend Chart */}
            <Grid size={{ xs: 12, lg: 8 }}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Daily Error Trend
                  </Typography>
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={dailyTrend}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" />
                      <YAxis yAxisId="left" />
                      <YAxis yAxisId="right" orientation="right" />
                      <RechartsTooltip />
                      <Line
                        yAxisId="left"
                        type="monotone"
                        dataKey="errors"
                        stroke="#f44336"
                        name="Errors"
                      />
                      <Line
                        yAxisId="left"
                        type="monotone"
                        dataKey="executions"
                        stroke="#2196f3"
                        name="Total Executions"
                      />
                      <Line
                        yAxisId="right"
                        type="monotone"
                        dataKey="error_rate"
                        stroke="#ff9800"
                        name="Error Rate (%)"
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </Grid>

            {/* Error Types Pie Chart */}
            <Grid size={{ xs: 12, lg: 4 }}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Error Types Distribution
                  </Typography>
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={errorTypes.map(et => ({ ...et, name: et.error_type }))}
                        dataKey="count"
                        nameKey="error_type"
                        cx="50%"
                        cy="50%"
                        outerRadius={80}
                        label={(entry: any) => `${entry.percentage}%`}
                      >
                        {errorTypes.map((_, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <RechartsTooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>

        {/* Error Types Tab */}
        <TabPanel value={activeTab} index={1}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Error Type</TableCell>
                  <TableCell align="right">Count</TableCell>
                  <TableCell align="right">Percentage</TableCell>
                  <TableCell align="right">Severity</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {errorTypes.map((errorType) => (
                  <TableRow key={errorType.error_type} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {getErrorTypeIcon(errorType.error_type)}
                        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                          {errorType.error_type}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" fontWeight="medium">
                        {errorType.count.toLocaleString()}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Chip
                        label={`${errorType.percentage}%`}
                        size="small"
                        color={errorType.percentage > 30 ? 'error' : 'default'}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Chip
                        label={getErrorTypeColor(errorType.error_type).toUpperCase()}
                        size="small"
                        color={getErrorTypeColor(errorType.error_type)}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        {/* By Expert Tab */}
        <TabPanel value={activeTab} index={2}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Expert Name</TableCell>
                  <TableCell>System</TableCell>
                  <TableCell align="right">Error Count</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {errorByExpert.map((expert) => (
                  <TableRow key={expert.expert_id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {expert.expert_name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {expert.system_name}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Chip
                        label={expert.error_count}
                        size="small"
                        color={expert.error_count > 10 ? 'error' : 'default'}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        {/* By System Tab */}
        <TabPanel value={activeTab} index={3}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>System Name</TableCell>
                  <TableCell align="right">Error Count</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {errorBySystem.map((system) => (
                  <TableRow key={system.system_id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {system.system_name}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Chip
                        label={system.error_count}
                        size="small"
                        color={system.error_count > 20 ? 'error' : 'default'}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        {/* By Agent Tab */}
        <TabPanel value={activeTab} index={4}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Agent Name</TableCell>
                  <TableCell align="right">Error Count</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data?.error_by_agent?.map((agent) => (
                  <TableRow key={agent.agent_id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {agent.agent_name}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Chip
                        label={agent.error_count}
                        size="small"
                        color={agent.error_count > 10 ? 'error' : 'default'}
                      />
                    </TableCell>
                  </TableRow>
                )) || (
                  <TableRow>
                    <TableCell colSpan={2} align="center">
                      <Typography variant="body2" color="text.secondary">
                        No agent errors found
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        {/* Recent Errors Tab */}
        <TabPanel value={activeTab} index={5}>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Time</TableCell>
                  <TableCell>Expert</TableCell>
                  <TableCell>System</TableCell>
                  <TableCell>Error Type</TableCell>
                  <TableCell>Error Message</TableCell>
                  <TableCell>Retry</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {recentErrors.map((error) => (
                  <TableRow key={error.id} hover>
                    <TableCell>
                      <Typography variant="caption">
                        {formatStoredDateTime(error.created_at, undefined, '-')}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{error.expert_name}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {error.system_name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={error.error_type}
                        size="small"
                        color={getErrorTypeColor(error.error_type)}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{
                          maxWidth: 300,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {error.error_message}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={`Attempt ${error.retry_attempt + 1}`}
                        size="small"
                        color={error.is_final_result ? 'error' : 'default'}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="View Details">
                        <IconButton
                          size="small"
                          onClick={() => setSelectedError(error.id)}
                        >
                          <ViewIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>
      </Paper>

      {/* Error Detail Dialog */}
      <Dialog
        open={!!selectedError}
        onClose={() => setSelectedError(null)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">Error Details</Typography>
            {errorDetail && (
              <Chip
                label={errorDetail.error_type}
                color={getErrorTypeColor(errorDetail.error_type)}
              />
            )}
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {isLoadingDetail ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : errorDetail ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {/* Basic Info */}
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Basic Information
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 6 }}>
                      <Typography variant="body2" color="text.secondary">Expert:</Typography>
                      <Typography variant="body1">{errorDetail.expert_name}</Typography>
                    </Grid>
                    <Grid size={{ xs: 6 }}>
                      <Typography variant="body2" color="text.secondary">System:</Typography>
                      <Typography variant="body1">{errorDetail.system_name}</Typography>
                    </Grid>
                    <Grid size={{ xs: 6 }}>
                      <Typography variant="body2" color="text.secondary">Time:</Typography>
                      <Typography variant="body1">
                        {formatStoredDateTime(errorDetail.created_at, undefined, '-')}
                      </Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              {/* Error Message */}
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Error Message
                  </Typography>
                  <Alert severity="error">
                    {errorDetail.error_message}
                  </Alert>
                </CardContent>
              </Card>

              {/* Retry Chain */}
              {errorDetail.retry_chain.length > 0 && (
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Retry Chain
                    </Typography>
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Attempt</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>Error Type</TableCell>
                            <TableCell>Error</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {errorDetail.retry_chain.map((retry) => (
                            <TableRow key={retry.id}>
                              <TableCell>#{retry.attempt + 1}</TableCell>
                              <TableCell>
                                <Chip
                                  label={retry.status}
                                  size="small"
                                  color={retry.status === 'ERROR' ? 'error' : 'default'}
                                />
                              </TableCell>
                              <TableCell>
                                <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                                  {retry.error_type}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="caption">
                                  {retry.error.substring(0, 100)}
                                  {retry.error.length > 100 && '...'}
                                </Typography>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </CardContent>
                </Card>
              )}

              {/* Prompt */}
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Prompt
                  </Typography>
                  <TextField
                    fullWidth
                    multiline
                    rows={6}
                    value={errorDetail.prompt}
                    InputProps={{
                      readOnly: true,
                      sx: { fontFamily: 'monospace', fontSize: '0.875rem' },
                    }}
                  />
                </CardContent>
              </Card>

              {/* Logs */}
              {errorDetail.logs && (
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Execution Logs
                    </Typography>
                    <TextField
                      fullWidth
                      multiline
                      rows={8}
                      value={errorDetail.logs}
                      InputProps={{
                        readOnly: true,
                        sx: { fontFamily: 'monospace', fontSize: '0.875rem' },
                      }}
                    />
                  </CardContent>
                </Card>
              )}

              {/* Metadata */}
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Metadata
                  </Typography>
                  <TextField
                    fullWidth
                    multiline
                    rows={8}
                    value={JSON.stringify(errorDetail.metadata, null, 2)}
                    InputProps={{
                      readOnly: true,
                      sx: { fontFamily: 'monospace', fontSize: '0.875rem' },
                    }}
                  />
                </CardContent>
              </Card>
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelectedError(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ErrorDashboard;
